import React, { useCallback, useState } from "react";
import WidgetCard from "../_shared/WidgetCard";
import ViewAllButton from "../_shared/ViewAllButton";
import ViewAllModal from "../modals/ViewAllModal";
import { CampusDatum, VIEW_ALL_PAGE_SIZE } from "../data";
import { getTeacherAdoption } from "shared/services/headOfficeService";
import { mapTeacherAdoption } from "../apiMappers";
import type { ViewAllPage } from "../modals/ViewAllModal";
import type { BlurTarget } from "../_shared/exportPdf";
import styles from "./style.module.scss";

interface TeacherAdoptionProps {
  campuses: CampusDatum[];
  /** reporting window so the modal fetches the same range */
  win: { from: string; to: string };
  /** copy overrides for campus-scoped reuse */
  title?: string;
  subtitle?: string;
  /** first column header (default "Campus") */
  entityLabel?: string;
  /** what the export may blur the row name as (default a campus) */
  nameBlur?: BlurTarget;
  /** metric column header (default "Active teachers") */
  metricLabel?: string;
  /** override the row's 0–1 bar fraction (default active/total teachers) */
  fracFor?: (c: CampusDatum) => number;
  /** override the row's trailing value (default "active / total") */
  valueFor?: (c: CampusDatum) => React.ReactNode;
  /** override the paginated fetch (campus reuse hits the campus endpoint) */
  fetchPage?: (page: number) => Promise<ViewAllPage>;
}

/** Active teaching staff by campus. Shows the top 5; "View All" paginates the rest. */
export default function TeacherAdoption({
  campuses,
  win,
  title = "Teacher Adoption",
  subtitle = "Active teaching staff by campus",
  entityLabel = "Campus",
  nameBlur = "campus",
  metricLabel = "Active teachers",
  fracFor,
  valueFor,
  fetchPage: fetchPageProp,
}: TeacherAdoptionProps) {
  const [open, setOpen] = useState(false);

  const renderRows = (list: CampusDatum[]) =>
    list.map((c) => {
      const frac = fracFor ? fracFor(c) : c.tTotal ? c.tActive / c.tTotal : 0;
      const barColor =
        frac >= 0.8 ? "#1897A6" : frac >= 0.5 ? "#F9A11B" : "#EF437B";
      return (
        <div className={styles.row} key={c.name}>
          <div className={styles.name} data-ho-blur={nameBlur}>
            {c.name}
          </div>
          <div className={styles.metric}>
            <div className={styles.track}>
              <div
                className={styles.fill}
                style={{
                  width: `${Math.round(frac * 100)}%`,
                  background: barColor,
                }}
              />
            </div>
            <span className={styles.value}>
              {valueFor ? valueFor(c) : `${c.tActive} / ${c.tTotal}`}
            </span>
          </div>
        </div>
      );
    });

  const header = (
    <div className={styles.headRow}>
      <div>{entityLabel}</div>
      <div className={styles.right}>{metricLabel}</div>
    </div>
  );

  const defaultFetchPage = useCallback(
    async (page: number) => {
      const res = await getTeacherAdoption({ ...win, per_page: VIEW_ALL_PAGE_SIZE, page });
      const data = res?.data?.data ?? {};
      const p = data?.pagination ?? {};
      return {
        rows: mapTeacherAdoption(data),
        total: Number(p.total) || 0,
        lastPage: Number(p.last_page) || 1,
        currentPage: Number(p.current_page) || page,
      };
    },
    [win.from, win.to],
  );

  const fetchPage = fetchPageProp ?? defaultFetchPage;

  return (
    <WidgetCard
      title={title}
      subtitle={subtitle}
      right={
        campuses.length > 5 ? (
          <ViewAllButton onClick={() => setOpen(true)} />
        ) : undefined
      }
      flush
    >
      {header}
      {renderRows(campuses.slice(0, 5))}

      {open && (
        <ViewAllModal
          title={title}
          subtitle={subtitle}
          onClose={() => setOpen(false)}
          fetchPage={fetchPage}
          renderContent={(rows) => (
            <>
              {header}
              {renderRows(rows as CampusDatum[])}
            </>
          )}
        />
      )}
    </WidgetCard>
  );
}
